import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button, Heading, Step, StepProps, useStepByStep } from 'yoso';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { T } from '~/components/I18n/Translation';
import circleCheck from '~/components/Icon/circle-check.png';
import { useLand } from '~/components/Land';
import { landMeta } from '~/components/Land/Land.meta';
import { OtherLandList } from '~/components/Land/Other/OtherLandList';
import { BottomSheetProps } from '~/components/Modal/BottomSheet';
import { Stepper } from '~/components/Stepper/Stepper';
import { W8benCard } from '~/components/Wallet/W8ben/Card/W8benCard';
import { registerW8benUrl, useW8ben } from '~/components/Wallet/W8ben/W8ben.hooks';
import stepStyles from '../Step/ParticipationStep.module.scss';
import successStyles from './ParticipationSuccess.module.scss';

export type ParticipationSuccessStepProps = Omit<StepProps, 'name'> & {
  className?: string;
  landId: number;
  zoneId: number;
  onConfirm?: BottomSheetProps['onConfirm'];
};

export const ParticipationSuccessStep = ({
  className,
  landId,
  zoneId,
  onConfirm,
  ...props
}: ParticipationSuccessStepProps) => {
  const { open } = useEmbeddedViewer();
  const { steps } = useStepByStep();
  const stepIdx = useMemo(() => steps.indexOf('SUCCESS'), [steps]);

  const {
    data: { status },
    isFetched,
  } = useW8ben();

  const {
    data: { landType, zoneList },
  } = useLand(landId);
  const zone = useMemo(() => zoneList.find((each) => each.zoneId === zoneId), [zoneId, zoneList]);
  const { zoneNumber } = zone || {};

  const handleW8benLinkClick = useCallback(
    (e) => {
      onConfirm?.(e);
      requestAnimationFrame(() => open(registerW8benUrl));
    },
    [open, onConfirm]
  );

  return (
    <Step name="SUCCESS" {...props}>
      <div
        className={classNames(stepStyles.root, className)}
        // data-event="impression"
        // data-event-category="land_join_successful_popup"
        // data-event-impression-id="{category}"
      >
        <div className={stepStyles.header}>
          <Stepper className={stepStyles.stepper} activeStep={stepIdx} length={steps.length} />
        </div>
        <div className={stepStyles.body}>
          <img
            className={stepStyles.image}
            alt="step3"
            src={circleCheck}
            width={140}
            height={140}
          />
          <Heading className={stepStyles.title} level={5}>
            <T name="citizen_land_join_complete_popup_title" />
          </Heading>
          <p className={stepStyles.description}>
            <T
              name="citizen_land_join_complete_popup_desc"
              balance
              options={[landMeta[landType].title, zoneNumber]}
            />
          </p>
          <div className={classNames(stepStyles.bottom, successStyles.root)}>
            {isFetched &&
              (status === 'REGISTERED' ? (
                <OtherLandList className={successStyles.landList} landId={landId} />
              ) : (
                <W8benCard
                  className={successStyles.w8benCard}
                  onClick={handleW8benLinkClick}
                  data-event="click"
                  data-event-target="land_popup_step3_w8ben_btn"
                />
              ))}
          </div>
        </div>
        <div className={stepStyles.footer}>
          <Button
            className={stepStyles.button}
            color="primary"
            onClick={onConfirm}
            data-event="click"
            data-event-target="land_popup_step3_done_btn">
            <T name="citizen_confirm_ok" />
          </Button>
        </div>
      </div>
    </Step>
  );
};
